/** @jsxRuntime classic */
/** @jsx jsx */
import type { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import StrokeWeightLargeIcon from '@atlaskit/icon/core/stroke-weight-large';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';

import { EllipsesAnimation } from '@atlassian/rovo-platform-ui-components/primitives/EllipsesAnimation';

import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';

import type { RecentWorkListItemLayoutProps } from '../../layouts/RecentWorkListItemLayout.tsx';
import { RecentWorkListItemLayout } from '../../layouts/RecentWorkListItemLayout.tsx';
import { recentWorkMessages } from '../../recentWorkMessages.ts';
import { getContentIcon, getContentTypeMessage } from '../recentWorkContent.tsx';

const styles = cssMap({
	title: {
		flexGrow: 1,
		maxWidth: '100%',
		minWidth: 0,
	},
	titleText: {
		display: 'block',
		overflow: 'hidden',
		color: token('color.text'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		width: '100%',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	metadata: {
		display: 'flex',
		alignItems: 'center',
		overflow: 'hidden',
		minWidth: 0,
		whiteSpace: 'nowrap',
	},
	separator: {
		flexShrink: 0,
		marginInlineStart: token('space.050'),
		marginInlineEnd: token('space.050'),
	},
	spaceLinkWrapper: {
		position: 'relative',
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		overflow: 'hidden',
		minWidth: 0,
		columnGap: token('space.050'),
	},
	agentMetadata: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		columnGap: token('space.050'),
	},
	agentPicture: {
		flexShrink: 0,
		objectFit: 'cover',
		borderRadius: token('radius.full'),
	},
	statusIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '24px',
		height: '24px',
	},
	sessionLocation: {
		display: 'flex',
		alignItems: 'center',
		minWidth: 0,
		columnGap: token('space.050'),
	},
	status: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
		flexShrink: 0,
		columnGap: token('space.050'),
		font: token('font.body.small'),
		minWidth: '112px',
		minHeight: '24px',
	},
	workingStatus: {
		color: token('color.text.subtle'),
	},
	needsInputStatus: {
		color: token('color.text.subtle'),
	},
	finishedStatus: {
		color: token('color.text.success'),
	},
	spaceLink: {
		overflow: 'hidden',
		color: token('color.text.subtlest'),
		textDecoration: 'none',
		textOverflow: 'ellipsis',
		'&:visited': {
			color: token('color.text.subtlest'),
			textDecoration: 'none',
		},
		'&:hover': {
			color: token('color.text.subtlest'),
			textDecoration: 'none',
		},
	},
});

export type AgentSessionCardStatus = 'working' | 'needs-input' | 'finished';

const AgentSessionStatus = ({ status }: { status: AgentSessionCardStatus }): JSX.Element => {
	switch (status) {
		case 'working':
			return (
				<div css={[styles.status, styles.workingStatus]} data-testid="agent-session-working-status">
					<FormattedMessage {...recentWorkMessages.working} />
					<EllipsesAnimation />
				</div>
			);
		case 'finished':
			return (
				<div
					css={[styles.status, styles.finishedStatus]}
					data-testid="agent-session-finished-status"
				>
					<FormattedMessage {...recentWorkMessages.finished} />
					<Box xcss={styles.statusIcon}>
						<StatusSuccessIcon
							label=""
							size="medium"
							color={token('color.icon.success')}
							testId="agent-session-finished-icon"
						/>
					</Box>
				</div>
			);
		case 'needs-input':
		default:
			return (
				<div
					css={[styles.status, styles.needsInputStatus]}
					data-testid="agent-session-needs-input-status"
				>
					<FormattedMessage {...recentWorkMessages.needsInput} />
					<Box xcss={styles.statusIcon}>
						<StrokeWeightLargeIcon
							label=""
							size="small"
							color={token('color.icon.information')}
							testId="agent-session-needs-input-icon"
						/>
					</Box>
				</div>
			);
	}
};

export type AgentSessionListItemViewProps = Pick<RecentWorkListItemLayoutProps, 'onClick'> & {
	title?: string | null;
	url?: string;
	agentName?: string | null;
	agentPicture?: string | null;
	contentType?: string;
	contentSubType?: string | null;
	space?: { name?: string | null; spaceKey?: string | null } | null;
	status?: AgentSessionCardStatus;
};

export const AgentSessionListItemView: FC<AgentSessionListItemViewProps> = ({
	title,
	url,
	onClick,
	agentName,
	agentPicture,
	contentType,
	contentSubType,
	space,
	status,
}) => {
	const { formatMessage } = useIntl();
	const titleContent = title || formatMessage(recentWorkMessages.agentSession);
	const resolvedAgentName = agentName || formatMessage(recentWorkMessages.rovoAgent);
	const contentTypeMessage =
		getContentTypeMessage(contentType, contentSubType) || recentWorkMessages.chat;
	const primary = (
		<Tooltip content={titleContent} position="bottom">
			{(tooltipProps) => (
				<Box xcss={styles.title} testId="agent-session-card-title" {...tooltipProps}>
					<span css={styles.titleText}>{titleContent}</span>
				</Box>
			)}
		</Tooltip>
	);
	const secondary = (
		<div css={styles.metadata}>
			<span css={styles.agentMetadata}>
				{agentPicture && (
					<img
						css={styles.agentPicture}
						src={agentPicture}
						width={12}
						height={12}
						alt=""
						data-testid="agent-session-agent-picture"
					/>
				)}
				<span data-testid="agent-session-agent-name">{resolvedAgentName}</span>
			</span>
			<Box as="span" xcss={styles.separator} aria-hidden="true">
				·
			</Box>
			<FormattedMessage {...contentTypeMessage} />
			{space?.name && (
				<span css={styles.sessionLocation}>
					<Box as="span" xcss={styles.separator} aria-hidden="true">
						·
					</Box>
					<Box as="span" xcss={styles.spaceLinkWrapper}>
						<SpacesIcon
							label=""
							size="small"
							color={token('color.icon.subtle')}
							testId="agent-session-space-icon"
						/>
						{space.spaceKey ? (
							<Anchor
								xcss={styles.spaceLink}
								href={SPACE_OVERVIEW.toUrl({ spaceKey: space.spaceKey })}
							>
								{space.name}
							</Anchor>
						) : (
							space.name
						)}
					</Box>
				</span>
			)}
		</div>
	);
	const row = (
		<RecentWorkListItemLayout
			url={url}
			onClick={onClick}
			ariaLabel={titleContent}
			testId="agent-session-list-item"
			icon={
				contentType ? (
					getContentIcon(contentType, contentSubType)
				) : (
					<RovoChatIcon
						label=""
						color={token('color.icon.subtle')}
						testId="agent-session-chat-icon"
					/>
				)
			}
			secondary={secondary}
			actions={status ? <AgentSessionStatus status={status} /> : undefined}
		>
			{primary}
		</RecentWorkListItemLayout>
	);

	return url ? (
		<HoverPreloader
			name="agent-session-list-item"
			preloadUrl={url}
			preloadOptions={PreloadingPresets.onlyJS}
		>
			{row}
		</HoverPreloader>
	) : (
		row
	);
};
